import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Plus, CheckSquare, Paperclip, CheckCircle2, Circle, AlignLeft, ChevronDown, Loader2, FileText, Trash2, Users, Tag, Clock, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { DossierTagsEditor } from "../DossierTags";
import { TicketMembers } from "./TicketMembers";
import { TicketDates } from "./TicketDates";
import { TicketChecklists, createChecklist } from "./TicketChecklists";
import { TicketActivity } from "./TicketActivity";
import { DossierLinker } from "./DossierLinker";
import { ArchiveTicketButton } from "./ArchivedTickets";
import { useAuth } from "../../lib/auth";
import { openTicketAttachment, downloadTicketAttachment } from "../../lib/ticketFiles";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

function Description({ ticket, basePath, onChange }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(ticket.description || "");
  const save = async () => {
    try {
      await api.patch(`${basePath}/${ticket.id}`, { description: val });
      setEdit(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  return (
    <div data-testid="trello-description">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-semibold text-norea-text"><AlignLeft className="w-5 h-5 text-norea-textMuted" /> Description</div>
        {!edit && <button onClick={() => { setVal(ticket.description || ""); setEdit(true); }} className="btn-secondary text-xs" data-testid="trello-description-edit">Modifier</button>}
      </div>
      {edit ? (
        <div className="pl-7 space-y-2">
          <textarea autoFocus value={val} onChange={(e) => setVal(e.target.value)} rows={6} className="input-field text-sm" data-testid="trello-description-input" />
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary text-xs" data-testid="trello-description-save">Enregistrer</button>
            <button onClick={() => setEdit(false)} className="btn-secondary text-xs">Annuler</button>
          </div>
        </div>
      ) : (
        <div onClick={() => setEdit(true)} className="pl-7 text-sm text-norea-text whitespace-pre-wrap cursor-text min-h-[2rem]" data-testid="trello-description-text">
          {ticket.description || <span className="text-norea-textMuted italic">Ajouter une description plus détaillée…</span>}
        </div>
      )}
    </div>
  );
}

function Attachments({ ticket, basePath, onChange }) {
  const atts = ticket.attachments || [];
  if (!atts.length) return null;
  const open = (a) => openTicketAttachment(basePath, ticket.id, a);
  const del = async (a) => {
    if (!window.confirm(`Supprimer « ${a.filename} » ?`)) return;
    try { await api.delete(`${basePath}/${ticket.id}/attachments/${a.id}`); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div data-testid="trello-attachments">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-semibold text-norea-text"><Paperclip className="w-5 h-5 text-norea-textMuted" /> Pièces jointes</div>
        {atts.some((a) => (a.content_type || "").startsWith("image/")) && (
          <button onClick={async () => { await api.patch(`${basePath}/${ticket.id}/cover`, { done: !!ticket.cover_hidden }); onChange(); }} className="btn-secondary text-xs" data-testid="trello-cover-toggle">
            {ticket.cover_hidden ? "Afficher la couverture" : "Retirer la couverture"}
          </button>
        )}
      </div>
      <div className="pl-7 space-y-1.5">
        {atts.map((a) => (
          <div key={a.id} className="group flex items-center gap-2 p-1.5 rounded hover:bg-norea-surfaceHover" data-testid={`trello-att-${a.id}`}>
            <span className="w-14 h-10 rounded bg-norea-surfaceHover flex items-center justify-center text-[10px] font-bold uppercase text-norea-textMuted">{(a.filename || "").split(".").pop()}</span>
            <button onClick={() => open(a)} className="flex-1 min-w-0 text-left text-sm truncate hover:underline"><FileText className="w-3.5 h-3.5 inline mr-1" />{a.filename}</button>
            <button onClick={() => downloadTicketAttachment(basePath, ticket.id, a)} className="text-norea-textMuted hover:text-norea-primary" title="Télécharger" data-testid={`trello-att-download-${a.id}`}><Download className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(a)} className="opacity-0 group-hover:opacity-100 text-norea-textMuted hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const ActionBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover text-sm text-norea-text transition-colors" data-testid={testid}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

export default function TrelloTicketModal({ ticketId, basePath, columns, users, onClose }) {
  const [t, setT] = useState(null);
  const isAdmin = useAuth().user?.role === "admin";
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);
  const membersRef = useRef(null);
  const datesRef = useRef(null);
  const tagsRef = useRef(null);
  const load = useCallback(() => api.get(`${basePath}/${ticketId}/full`).then((r) => { setT(r.data); setTitle(r.data.title); }).catch(() => toast.error("Ticket introuvable")), [basePath, ticketId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && !document.querySelector("[data-radix-popper-content-wrapper]") && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const act = async (fn, ok) => { try { await fn(); await load(); if (ok) toast.success(ok); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };
  const upload = (files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    act(() => api.post(`${basePath}/${t.id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } }), "Pièce jointe ajoutée");
  };
  const addChecklist = async () => {
    const cls = await createChecklist(basePath, t.id, window.prompt("Titre de la checklist", "Checklist"));
    if (cls) load();
  };
  const click = (ref, sel = "button") => setTimeout(() => ref.current?.querySelector(sel)?.click(), 120);
  const col = columns.find((c) => c.id === t?.status);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl border border-norea-border flex flex-col max-h-[calc(100vh-4rem)]" onClick={(e) => e.stopPropagation()} data-testid="trello-ticket-modal">
        <div className="px-6 py-3 border-b border-norea-border flex items-center justify-between">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-norea-surfaceHover hover:bg-norea-border text-sm font-semibold uppercase tracking-wide" data-testid="trello-column-select">
                {col?.label || "—"} <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {columns.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => act(() => api.patch(`${basePath}/${t.id}/status`, { status: c.id }))} data-testid={`trello-column-option-${c.id}`}>{c.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-1">
            {isAdmin && t && <ArchiveTicketButton basePath={basePath} ticketId={t.id} onDone={onClose} />}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-norea-surfaceHover" data-testid="trello-close"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {!t ? <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[3fr_2fr]">
            <div className="p-6 space-y-6 overflow-y-auto">
              <div className="flex items-start gap-3">
                <button onClick={() => act(() => api.patch(`${basePath}/${t.id}/complete`, { done: !t.due_done }))} className="mt-1.5 transition-transform hover:scale-110" title={t.due_done ? "Marquer non terminé" : "Marquer terminé"} data-testid="trello-complete-toggle" data-done={t.due_done ? "1" : "0"}>
                  {t.due_done ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <Circle className="w-6 h-6 text-norea-textMuted" />}
                </button>
                <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  onBlur={() => title.trim() && title !== t.title ? act(() => api.patch(`${basePath}/${t.id}`, { title: title.trim() })) : setTitle(t.title)}
                  className="flex-1 font-heading text-2xl md:text-3xl font-semibold text-norea-text bg-transparent rounded px-1 focus:outline-none focus:bg-white focus:ring-2 focus:ring-norea-primary" data-testid="trello-title" />
              </div>
              <div className="flex flex-wrap gap-2 pl-9">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-norea-border bg-white hover:bg-norea-surfaceHover text-sm" data-testid="trello-add-menu"><Plus className="w-4 h-4" /> Ajouter</button></DropdownMenuTrigger>
                  <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
                    <DropdownMenuItem onClick={() => click(membersRef, "[data-testid=trello-members-add]")}><Users className="w-4 h-4 mr-2" /> Membres</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => click(tagsRef, "[data-testid=btn-add-tag]")}><Tag className="w-4 h-4 mr-2" /> Étiquettes</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => click(datesRef, "[data-testid=trello-dates-btn]")}><Clock className="w-4 h-4 mr-2" /> Dates</DropdownMenuItem>
                    <DropdownMenuItem onClick={addChecklist}><CheckSquare className="w-4 h-4 mr-2" /> Checklist</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => fileRef.current?.click()}><Paperclip className="w-4 h-4 mr-2" /> Pièce jointe</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ActionBtn icon={CheckSquare} label="Checklist" onClick={addChecklist} testid="trello-add-checklist" />
                <ActionBtn icon={Paperclip} label="Pièce jointe" onClick={() => fileRef.current?.click()} testid="trello-add-attachment" />
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) upload(e.target.files); e.target.value = ""; }} data-testid="trello-file-input" />
              </div>
              <div className="pl-9 flex flex-wrap gap-x-8 gap-y-4">
                <div ref={membersRef}><TicketMembers ticket={t} basePath={basePath} users={users} onChange={load} /></div>
                <div ref={tagsRef} className="[&_[data-testid=btn-add-tag]]:order-last" data-testid="trello-tags">
                  <div className="text-xs font-semibold text-norea-textMuted mb-1.5">Étiquettes</div>
                  <DossierTagsEditor endpoint={`${basePath}/${t.id}/tags`} ids={t.tags || []} onChange={load} />
                </div>
                <div ref={datesRef}><TicketDates key={`${t.due_at}-${t.start_at}`} ticket={t} basePath={basePath} onChange={load} /></div>
              </div>
              <div className="pl-9"><DossierLinker basePath={basePath} ticketId={t.id} initial={t.dossiers} onChange={load} /></div>
              <Description ticket={t} basePath={basePath} onChange={load} />
              <Attachments ticket={t} basePath={basePath} onChange={load} />
              <TicketChecklists ticket={t} basePath={basePath} setCls={(cls) => { setT((x) => ({ ...x, checklists: cls })); api.get(`${basePath}/${ticketId}/full`).then((r) => setT((x) => ({ ...x, activity: r.data.activity }))).catch(() => {}); }} />
            </div>
            <div className="p-6 bg-norea-bg border-t lg:border-t-0 lg:border-l border-norea-border overflow-y-auto">
              <TicketActivity ticket={t} entityType={basePath.includes("internal") ? "internal_ticket" : "ticket"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
